import { isEmpty, isString, uniq } from 'lodash';
import { getFilenameParts } from '@shared/util/media/type';

/**
 * What Ask accepts, and the one place that decides it.
 *
 * The canonical names below are exactly the gateway's `SUPPORTED_ATTACHMENT_MIME_TYPES`, and that
 * is not a coincidence to be relaxed: `attachments[].mimeType` is a `z.enum`, so one unsupported
 * file does not lose that file, it rejects the whole request — question, other attachments and all.
 * Refusing here, at pick time, is what turns that into a named file the user can take out.
 *
 * **No audio and no video.** The gateway takes neither on this surface, so a recording is refused
 * however it arrives — see `ASK_RECORDING_CONTENT_TYPES`. **No HEIC/HEIF**: iOS converts to JPEG
 * when a photo is picked from Photos, but a `.heic` out of the Files app is refused.
 *
 * A file arrives with a content type the browser guessed from its extension, and that guess is
 * wrong often enough that the extension has to be able to overrule it — Windows reports `.csv`
 * as `application/vnd.ms-excel`, and `.md` usually arrives as an empty string. So there are two
 * lookups, `CANONICAL_CONTENT_TYPES` then `EXTENSION_CONTENT_TYPES`, and everything that needs
 * to know whether a file is allowed goes through `askAttachmentContentType`.
 */

/**
 * Content types we accept, mapped to the one name we send onward. Aliases are the same format
 * under a name some platform reports instead: Windows and older Safari say `image/jpg`, older
 * editors say `text/x-markdown`.
 *
 * Markdown and CSV map to `text/plain` rather than being refused. Both *are* plain text, the
 * gateway has no enum member for either, and the file's own name goes up as
 * `attachments[].fileName` — so the model is told it is reading `expenses.csv` and gets its
 * contents, which is the whole of what the narrower type would have conveyed.
 *
 * Formats that cannot be honestly narrowed are gone rather than mapped: `.docx`/`.xlsx` are zip
 * containers, not text, and calling them `text/plain` would hand the model a binary blob.
 */
const CANONICAL_CONTENT_TYPES: Record<string, string> = {
  'image/jpeg': 'image/jpeg',
  'image/jpg': 'image/jpeg',
  'image/png': 'image/png',
  'image/gif': 'image/gif',
  'image/webp': 'image/webp',

  'application/pdf': 'application/pdf',

  'text/plain': 'text/plain',
  'text/markdown': 'text/plain',
  'text/x-markdown': 'text/plain',
  'text/csv': 'text/plain',
};

/** The fallback for files whose reported content type is empty or wrong. */
const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',

  pdf: 'application/pdf',

  txt: 'text/plain',
  md: 'text/plain',
  markdown: 'text/plain',
  csv: 'text/plain',
};

/** Every content type Ask will take, canonical names and aliases alike. */
export const ASK_ATTACHMENT_CONTENT_TYPES = Object.keys(CANONICAL_CONTENT_TYPES);

/**
 * What goes in a file input's `accept`. Extensions as well as content types, because that is
 * the only thing a browser can match on for a format it does not have a type for — which is
 * exactly the `.md` and `.csv` case the extension fallback exists for.
 *
 * Only a hint either way: every platform lets the user switch the dialog to "All Files", which
 * is why `askAttachmentContentType` is checked again after the picker returns.
 */
export const ASK_ATTACHMENT_ACCEPT = uniq([
  ...ASK_ATTACHMENT_CONTENT_TYPES,
  ...Object.keys(EXTENSION_CONTENT_TYPES).map(extension => `.${extension}`),
]);

/**
 * The gateway's `MAX_ATTACHMENTS`. Over it is a rejected request rather than a truncated one, so
 * this is a hard ceiling and not a presentation choice — the tray refuses the fifth file by name
 * (see `rejectionMessage`) instead of sending a turn the gateway will throw away.
 */
export const ASK_MAX_ATTACHMENTS = 4;

/** Comfortably over a photo or a scanned PDF, under what an agent can be asked to read. */
export const ASK_MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/**
 * When a recording stops itself. Long enough for anything worth describing out loud, and short
 * enough that a microphone left open by accident cannot produce a file the size cap then refuses
 * — which would lose the recording after the user had already made it.
 */
export const ASK_MAX_RECORDING_MS = 10 * 60 * 1000;

/**
 * Containers to record into, best first. Safari supports only MP4, everything else prefers Opus in
 * WebM; the plain WebM entry is the fallback for a browser that reports no codec support at all.
 *
 * **None of these is an accepted attachment.** The gateway's enum has no audio member, so a
 * recording cannot be uploaded for it to hear — which is why `ASK_VOICE_INPUT_MODE` is
 * `'transcribe'` and why flipping it to `'record'` no longer works. Kept because the recorder it
 * feeds is still whole and the constraint is the gateway's rather than the browser's; see
 * `../voice/config.ts`.
 */
export const ASK_RECORDING_CONTENT_TYPES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];

/** Strips the parameters off a media type: `audio/webm;codecs=opus` is `audio/webm` to us. */
function baseContentType(contentType: string): string {
  return contentType.split(';')[0].trim().toLowerCase();
}

/**
 * The content type to put on a recording, given what `MediaRecorder` reports.
 *
 * Deliberately not `askAttachmentContentType`: this only drops the codec parameter, which means
 * nothing downstream, and makes no judgement about whether the file is acceptable. It cannot,
 * because no audio type is — see `ASK_RECORDING_CONTENT_TYPES`. Routing the recorder through the
 * allowlist instead would refuse every recording at the moment it was finished, which is the one
 * point at which the audio cannot be recovered.
 */
export function recordingContentType(mimeType: string): string {
  return baseContentType(mimeType);
}

/**
 * The content type to send onward for a picked file, or undefined when Ask will not take it.
 *
 * Also the value handed to `uploadFile`, which otherwise falls back to
 * `application/octet-stream` and loses the one thing the agent needs in order to know how to
 * read the file.
 */
export function askAttachmentContentType(file: {
  name: string;
  contentType?: string | null;
}): string | undefined {
  const reported = isString(file.contentType) ? baseContentType(file.contentType) : '';
  const byContentType = CANONICAL_CONTENT_TYPES[reported];
  if (!isEmpty(byContentType)) {
    return byContentType;
  }

  // The reported type was empty, or a type we do not take under a name that might still be a
  // format we do. Either way the extension gets the last word.
  //
  // `getFilenameParts` returns the whole name as the extension when there is no dot in it, so
  // an extensionless file called `png` would otherwise be read as an image.
  if (!file.name.includes('.')) {
    return undefined;
  }
  const [, extension] = getFilenameParts(file.name);
  if (!isString(extension)) {
    return undefined;
  }
  return EXTENSION_CONTENT_TYPES[extension.toLowerCase()];
}

/** Whether Ask will take this file at all. */
export function isAskAttachment(file: { name: string; contentType?: string | null }): boolean {
  return !isEmpty(askAttachmentContentType(file));
}

/**
 * Whether to treat a file as audio — a recording or a voice memo, shown with a duration rather
 * than a thumbnail.
 *
 * Reads the raw content type rather than going through `askAttachmentContentType`, which no longer
 * has an audio answer to give: audio is not an accepted attachment, but `addRecording` stages a
 * recording without consulting the allowlist, so this still has to recognise one when it sees it.
 * A transcript stored while `ASK_VOICE_INPUT_MODE` was `'record'` also still holds audio parts, and
 * they should keep rendering as what they are.
 *
 * Local to Ask on purpose. `getMediaType` in `@shared/util/media/type` has no audio case, and
 * adding one would change which renderer every existing chat audio attachment gets, on a
 * surface this has nothing to do with.
 */
export function isAskAudio(file: { name: string; contentType?: string | null }): boolean {
  return isString(file.contentType) && baseContentType(file.contentType).startsWith('audio/');
}
